import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { invoiceAPI, accountAPI, paymentAPI, notificationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatINR, formatDate, todayISO, apiError } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import DatePicker from '../components/DatePicker';
import { PAYMENT_METHODS } from '../components/ReceivePaymentDialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Edit2,
  Trash2,
  Loader2,
  CircleDollarSign,
  X,
  Send,
  BellRing,
  Ban,
  RotateCcw,
  Mail,
  MessageCircle,
  FileText,
  MoreHorizontal,
} from 'lucide-react';

const STATUS_STYLES = {
  draft:   'bg-gray-100 text-gray-700',
  sent:    'bg-gray-200 text-gray-800',
  paid:    'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500 line-through',
};

const SOURCE_STYLES = {
  internal:  'bg-black text-white',
  sleekbill: 'bg-gray-200 text-gray-800',
};

// Message log statuses (monochrome): failures stand out in solid black.
const MSG_STYLES = {
  sent:    'bg-gray-200 text-gray-800',
  failed:  'bg-black text-white',
  skipped: 'bg-gray-100 text-gray-500',
  sending: 'bg-gray-100 text-gray-500',
  unknown: 'bg-gray-200 text-gray-800 border border-black',
};

const CHANNEL_LABEL = { email: 'Email', whatsapp: 'WhatsApp' };

const fmtWhen = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');

const pdfName = (inv) => `invoice_${(inv.invoice_number || inv.id.slice(0, 8)).replace(/[^\w.-]+/g, '-')}.pdf`;

const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

function TotalRow({ label, value, muted }) {
  return (
    <div className="flex justify-between text-sm gap-6">
      <span className="text-gray-600">{label}</span>
      <span className={`font-mono ${muted ? 'text-gray-600' : ''}`}>{value}</span>
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${MSG_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {status === 'unknown' ? 'unconfirmed' : status}
    </span>
  );
}

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfError, setPdfError] = useState(false);
  const pdfUrlRef = useRef('');
  const [accounts, setAccounts] = useState([]);
  const [payments, setPayments] = useState({ internal: [], sleekbill: [] });
  const [messages, setMessages] = useState([]);

  // Record-Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: todayISO(), account_id: '', method: '', reference: '', notes: '' });
  const [paySaving, setPaySaving] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  // Send invoice / reminder dialog
  const [sendMode, setSendMode] = useState(null); // 'invoice' | 'reminder' | null
  const [sendForm, setSendForm] = useState({ email: true, whatsapp: true, emailTo: '', phoneTo: '' });
  const [sendPrefilling, setSendPrefilling] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [notifStatus, setNotifStatus] = useState(null);
  const [recip, setRecip] = useState(null); // { email, whatsapp } as the server resolved them

  const loadPdf = useCallback(async () => {
    setPdfError(false);
    try {
      const pdfRes = await invoiceAPI.downloadPdf(id);
      const url = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = url;
      setPdfUrl(url);
    } catch {
      setPdfError(true);
    }
  }, [id]);

  const loadMessages = useCallback(async () => {
    try {
      setMessages((await invoiceAPI.notifications(id)).data || []);
    } catch {
      setMessages([]);
    }
  }, [id]);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const [invRes, payRes, accRes] = await Promise.all([
        invoiceAPI.getOne(id),
        invoiceAPI.listPayments(id),
        accountAPI.getAll(),
      ]);
      setInvoice(invRes.data);
      setPayments(payRes.data || { internal: [], sleekbill: [] });
      setAccounts(accRes.data || []);
    } catch (err) {
      setInvoice(null);
      setLoadError(err.response?.status === 404 ? 'Invoice not found.' : apiError(err, 'Failed to load invoice'));
      return;
    } finally {
      setLoading(false);
    }
    loadMessages();
    loadPdf();
  }, [id, loadMessages, loadPdf]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); }, []);

  // Auto-open Record Payment dialog when navigated with ?action=pay
  useEffect(() => {
    if (!loading && invoice && searchParams.get('action') === 'pay' && invoice.balance_due > 0.01 && invoice.status !== 'cancelled' && can('write')) {
      openPay();
      // strip the param so refresh doesn't re-open
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoice]);

  // Auto-open the Send dialog when navigated with ?action=send (the form's "Save & send")
  useEffect(() => {
    if (!loading && invoice && searchParams.get('action') === 'send' && invoice.status !== 'cancelled' && can('write')) {
      openSend('invoice', searchParams.get('channel') || 'both');
      searchParams.delete('action');
      searchParams.delete('channel');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, invoice]);

  const openPay = () => {
    setPayForm({
      amount: invoice?.balance_due > 0 ? String(invoice.balance_due) : '',
      payment_date: todayISO(),
      account_id: '',
      method: '',
      reference: '',
      notes: '',
    });
    setPayOpen(true);
  };

  const submitPay = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payForm.amount);
    const balance = Number(invoice.balance_due) || 0;
    if (!amt || amt <= 0) return toast.error('Enter a positive amount');
    if (amt > balance + 0.01) return toast.error(`Amount is more than the balance due (${formatINR(balance)}).`);
    if (!payForm.payment_date) return toast.error('Pick a payment date');
    setPaySaving(true);
    try {
      await invoiceAPI.recordPayment(id, {
        amount: amt,
        payment_date: payForm.payment_date,
        account_id: payForm.account_id || null,
        method: payForm.method,
        reference: payForm.reference,
        notes: payForm.notes,
      });
      toast.success('Payment recorded');
      setPayOpen(false);
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Failed to record payment'));
    } finally {
      setPaySaving(false);
    }
  };

  const deleteInternalPayment = async (pid) => {
    if (!window.confirm('Delete this payment? Invoice balance will be recomputed.')) return;
    try {
      await paymentAPI.delete(pid);
      toast.success('Payment deleted');
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  // Cancel keeps the number in the series; the server answers 409 (with a reason) if payments exist.
  const handleCancel = async () => {
    if (!window.confirm(`Cancel invoice ${invoice.invoice_number}? The number stays in the series, and the invoice drops out of totals, reminders and statements.`)) return;
    setCancelBusy(true);
    try {
      await invoiceAPI.cancel(id);
      toast.success('Invoice cancelled');
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Could not cancel the invoice'));
    } finally {
      setCancelBusy(false);
    }
  };

  const handleReinstate = async () => {
    if (!window.confirm(`Reinstate invoice ${invoice.invoice_number}? It becomes active again.`)) return;
    setCancelBusy(true);
    try {
      await invoiceAPI.reinstate(id);
      toast.success('Invoice reinstated');
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Could not reinstate the invoice'));
    } finally {
      setCancelBusy(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await invoiceAPI.downloadPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfName(invoice);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, 'Download failed'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? Recorded payments will also be removed.`)) return;
    try {
      await invoiceAPI.delete(id);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  // channel: 'email' | 'whatsapp' | 'both'
  const openSend = async (mode, channel = 'both') => {
    setSendMode(mode);
    setSendResults(null);
    setRecip(null);
    setSendForm({ email: channel !== 'whatsapp', whatsapp: channel !== 'email', emailTo: '', phoneTo: '' });
    setSendPrefilling(true);
    // Status calls Meta (up to 30s each), so it only feeds the warnings and never blocks sending.
    if (!notifStatus) notificationsAPI.status().then((r) => setNotifStatus(r.data)).catch(() => {});
    try {
      // The server applies the same fallbacks the sender uses (secondary contacts, the WhatsApp number field).
      const r = (await invoiceAPI.recipients(id)).data || {};
      setRecip(r);
      setSendForm((f) => ({ ...f, emailTo: r.email || '', phoneTo: r.whatsapp || r.whatsapp_display || '' }));
    } catch {
      // Couldn't look the client up: leave the fields empty for the user to fill in.
    }
    setSendPrefilling(false);
  };

  const downloadSentPdf = async (pdfId) => {
    try {
      const res = await invoiceAPI.downloadDocument(id, pdfId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pdfName(invoice).replace(/\.pdf$/, '')}_as-sent.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, 'Could not download that copy'));
    }
  };

  const submitSend = async (e) => {
    e.preventDefault();
    const channels = ['email', 'whatsapp'].filter((c) => sendForm[c]);
    if (!channels.length) return toast.error('Pick Email and/or WhatsApp');
    const body = { channels };
    if (sendForm.email) body.email = sendForm.emailTo.trim();
    if (sendForm.whatsapp) body.phone = sendForm.phoneTo.trim();
    setSending(true);
    try {
      const res = sendMode === 'reminder'
        ? await invoiceAPI.remind(id, body)
        : await invoiceAPI.send(id, body);
      const results = res.data?.results || [];
      setSendResults(results);
      const ok = results.filter((r) => r.status === 'sent').length;
      const summary = results
        .map((r) => `${CHANNEL_LABEL[r.channel] || r.channel}: ${r.status}${r.error ? ` (${r.error})` : ''}`)
        .join(' · ') || 'Nothing was sent';
      if (ok && ok === results.length) toast.success(summary);
      else if (ok) toast.warning(summary);
      else toast.error(summary);
      if (res.data?.invoice) setInvoice(res.data.invoice);
      loadMessages();
    } catch (err) {
      toast.error(apiError(err, 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
        <p className="text-gray-700 font-medium">{loadError || 'Invoice not found.'}</p>
        <div className="flex gap-2">
          <Link to="/invoices">
            <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back to invoices</Button>
          </Link>
          {loadError !== 'Invoice not found.' && (
            <Button size="sm" className="bg-black hover:bg-black/90 text-white" onClick={() => { setLoading(true); load(); }}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Combined chronological history for display
  const allPayments = [
    // Imported Sleekbill payments now arrive in `internal` with source 'sleekbill'.
    ...(payments.internal || []).map(p => ({ ...p, _source: p.source === 'sleekbill' ? 'sleekbill' : 'internal' })),
    ...(payments.sleekbill || []).map(p => ({ ...p, _source: 'sleekbill' })),
  ].sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));

  const cancelled = invoice.status === 'cancelled';
  const canRecord = can('write') && !cancelled && invoice.balance_due > 0.01;
  const canRemind = can('write') && !cancelled && invoice.balance_due > 0.01 && invoice.status !== 'draft';
  const lines = invoice.line_items || [];
  const hasDiscount = lines.some((l) => Number(l.discount_pct) > 0);
  const roundOff = Number(invoice.round_off) || 0;
  const taxRows = [['CGST', invoice.cgst], ['SGST', invoice.sgst], ['IGST', invoice.igst]].filter(([, v]) => Number(v) > 0);
  const openAccounts = accounts.filter((a) => a.status !== 'closed');

  // Channel warnings for the send dialog (from /notifications/status).
  const wa = notifStatus?.whatsapp;
  const tplKey = sendMode === 'reminder' ? 'reminder_template' : 'invoice_template';
  const emailWarn = notifStatus && !notifStatus.email?.configured
    ? "Email isn't set up yet (Resend key missing)."
    : '';
  const waWarn = !notifStatus ? ''
    : !wa?.configured ? "WhatsApp isn't set up yet (Meta access token missing)."
    : wa[tplKey] !== 'APPROVED'
      ? `WhatsApp ${sendMode === 'reminder' ? 'reminder' : 'invoice'} template ${wa[tplKey] === 'REJECTED' ? 'was rejected by Meta' : 'is awaiting Meta approval'}.`
      : '';

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/invoices">
            <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] break-all">{invoice.invoice_number}</h1>
            <p className="text-sm text-gray-500">{invoice.client_name}</p>
          </div>
          <span
            data-testid="invoice-status-badge"
            className={`px-2 py-0.5 rounded-full text-xs font-medium ml-2 ${STATUS_STYLES[invoice.status] || 'bg-gray-100 text-gray-600'}`}
          >
            {cancelled ? 'Cancelled' : invoice.status}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canRecord && (
            <Button onClick={openPay} className="bg-black hover:bg-black/90 text-white">
              <CircleDollarSign className="w-4 h-4 mr-1" /> Record payment
            </Button>
          )}
          {can('write') && !cancelled && (
            <>
              <Button variant="outline" onClick={() => openSend('invoice', 'email')} data-testid="send-email-btn">
                <Mail className="w-4 h-4 mr-1" /> Email
              </Button>
              <Button variant="outline" onClick={() => openSend('invoice', 'whatsapp')} data-testid="send-whatsapp-btn">
                <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-1" /> Download PDF
          </Button>
          {(canRemind || can('write') || can('delete')) && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" aria-label="More actions" data-testid="invoice-more-btn">
                  <MoreHorizontal className="w-4 h-4" /><span className="ml-1">More</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {canRemind && (
                  <DropdownMenuItem onSelect={() => setTimeout(() => openSend('reminder'), 0)} data-testid="invoice-remind-item">
                    <BellRing className="w-4 h-4 mr-2" /> Send reminder
                  </DropdownMenuItem>
                )}
                {can('write') && !cancelled && (
                  <DropdownMenuItem onSelect={() => navigate(`/invoices/${id}/edit`)} data-testid="invoice-edit-item">
                    <Edit2 className="w-4 h-4 mr-2" /> Edit invoice
                  </DropdownMenuItem>
                )}
                {can('write') && !cancelled && !(Number(invoice.payment_made) > 0.01) && (
                  <DropdownMenuItem onSelect={handleCancel} disabled={cancelBusy} data-testid="invoice-cancel-btn">
                    <Ban className="w-4 h-4 mr-2" /> Cancel invoice
                  </DropdownMenuItem>
                )}
                {can('write') && cancelled && (
                  <DropdownMenuItem onSelect={handleReinstate} disabled={cancelBusy} data-testid="invoice-reinstate-btn">
                    <RotateCcw className="w-4 h-4 mr-2" /> Reinstate invoice
                  </DropdownMenuItem>
                )}
                {can('delete') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={handleDelete} className="text-red-700 focus:text-red-700" data-testid="invoice-delete-item">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete invoice
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {cancelled && (
        <div className="rounded-md border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-600" data-testid="invoice-cancelled-banner">
          <span className="font-medium text-gray-800">This invoice is cancelled</span>
          {invoice.cancelled_at ? ` on ${formatDate(invoice.cancelled_at)}` : ''}. Its number stays in the series; it is left out of totals, reminders and statements.
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
          <p className={`text-base sm:text-xl font-bold font-mono mt-1 break-words ${cancelled ? 'line-through text-gray-400' : ''}`}>{formatINR(invoice.total)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Paid</p>
          <p className="text-base sm:text-xl font-bold font-mono mt-1 text-green-700 break-words">{formatINR(invoice.payment_made)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Balance</p>
          <p className={`text-base sm:text-xl font-bold font-mono mt-1 break-words ${invoice.balance_due > 0 ? 'text-red-700' : 'text-gray-400'}`}>
            {formatINR(invoice.balance_due)}
          </p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Due</p>
          <p className="text-base sm:text-xl font-bold font-mono mt-1 whitespace-nowrap">{formatDate(invoice.due_at)}</p>
        </CardContent></Card>
      </div>

      {/* Line items + totals */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Invoice details</h2>
            {invoice.po_number && (
              <p className="text-sm" data-testid="invoice-po-number">
                <span className="text-gray-500">PO number </span>
                <span className="font-mono font-medium">{invoice.po_number}</span>
              </p>
            )}
          </div>
          {lines.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm sm:min-w-[520px]">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-4 w-[40px]">#</th>
                    <th className="py-2 pr-4">Item</th>
                    <th className="py-2 pr-4 text-right hidden sm:table-cell">Qty</th>
                    <th className="py-2 pr-4 text-right hidden sm:table-cell">Rate</th>
                    {hasDiscount && <th className="py-2 pr-4 text-right hidden sm:table-cell">Discount</th>}
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const pct = Number(l.discount_pct) || 0;
                    return (
                      <tr key={i} className="border-b last:border-0 align-top">
                        <td className="py-2 pr-4 text-gray-500 text-xs">{i + 1}</td>
                        <td className="py-2 pr-4">
                          <p className="font-medium">{l.name}</p>
                          {l.description && <p className="text-xs text-gray-500 whitespace-pre-line">{l.description}</p>}
                          <p className="sm:hidden text-xs text-gray-500 font-mono mt-0.5">
                            {l.quantity} × {formatINR(l.rate)}{pct > 0 ? ` · −${pct}%` : ''}
                          </p>
                        </td>
                        <td className="py-2 pr-4 text-right font-mono hidden sm:table-cell">{l.quantity}</td>
                        <td className="py-2 pr-4 text-right font-mono hidden sm:table-cell">{formatINR(l.rate)}</td>
                        {hasDiscount && (
                          <td className="py-2 pr-4 text-right font-mono text-gray-600 hidden sm:table-cell">
                            {pct > 0 ? `${pct}% (−${formatINR(round2((Number(l.amount) || 0) * pct / 100))})` : '—'}
                          </td>
                        )}
                        <td className="py-2 text-right font-mono whitespace-nowrap">{formatINR(l.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="ml-auto w-full sm:w-80 space-y-1.5" data-testid="invoice-totals">
            <TotalRow label="Subtotal" value={formatINR(invoice.subtotal)} />
            {Number(invoice.discount_amount) > 0 && (
              <TotalRow label="Discount" value={`−${formatINR(invoice.discount_amount)}`} muted />
            )}
            {taxRows.map(([label, v]) => <TotalRow key={label} label={label} value={formatINR(v)} muted />)}
            {Math.abs(roundOff) > 0.001 && (
              <TotalRow label="Round off" value={`${roundOff > 0 ? '+' : ''}${formatINR(roundOff)}`} muted />
            )}
            <div className="flex justify-between items-center bg-black text-white rounded-md px-3 py-2 mt-2">
              <span className="font-semibold">Total</span>
              <span className="font-mono font-bold">{formatINR(invoice.total)}</span>
            </div>
          </div>
          {invoice.private_notes && (
            <div className="border rounded-md bg-gray-50 p-3" data-testid="invoice-private-notes">
              <p className="text-xs uppercase tracking-wide text-gray-500">Internal note — not printed</p>
              <p className="text-sm mt-1 whitespace-pre-wrap break-words">{invoice.private_notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment history */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Payment history</h2>
            {canRecord && (
              <Button size="sm" variant="outline" onClick={openPay}>
                <CircleDollarSign className="w-4 h-4 mr-1" /> Record payment
              </Button>
            )}
          </div>
          {allPayments.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No payments recorded yet.</p>
          ) : (
            <>
            <ul className="sm:hidden divide-y" data-testid="payments-phone-list">
              {allPayments.map((p) => (
                <li key={`m-${p._source}-${p.id || p.payment_date}-${p.amount}`} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 text-sm">
                    <p className="font-medium">{formatDate(p.payment_date)}{p.payment_number ? <span className="ml-2 font-mono text-xs text-gray-500">#{p.payment_number}</span> : null}</p>
                    <p className="text-xs text-gray-500 break-words">
                      {[p.method, p.account_name, p.reference].filter(Boolean).join(' · ') || '—'}
                    </p>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${SOURCE_STYLES[p._source]}`}>{p._source}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="font-mono text-sm whitespace-nowrap">{formatINR(p.amount)}</span>
                    {p._source === 'internal' && can('delete') && (
                      <Button variant="ghost" size="icon" onClick={() => deleteInternalPayment(p.id)} className="text-red-700 h-9 w-9" aria-label="Delete payment">
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-4">Receipt #</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Method</th>
                    <th className="py-2 pr-4">Amount</th>
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allPayments.map((p) => (
                    <tr key={`${p._source}-${p.id || p.payment_date}-${p.amount}`} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-mono text-xs">{p.payment_number ? `#${p.payment_number}` : '—'}</td>
                      <td className="py-2 pr-4">{formatDate(p.payment_date)}</td>
                      <td className="py-2 pr-4 text-gray-600">{p.method || '—'}</td>
                      <td className="py-2 pr-4 font-mono">{formatINR(p.amount)}</td>
                      <td className="py-2 pr-4 text-gray-600">{p.account_name || '—'}</td>
                      <td className="py-2 pr-4 text-gray-600">{p.reference || '—'}</td>
                      <td className="py-2 pr-4">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${SOURCE_STYLES[p._source]}`}>
                          {p._source}
                        </span>
                        {p.created_by_name && p._source === 'internal' && (
                          <span className="ml-2 text-xs text-gray-400">by {p.created_by_name}</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {p._source === 'internal' && can('delete') && (
                          <Button variant="ghost" size="sm" onClick={() => deleteInternalPayment(p.id)} className="text-red-700" title="Delete payment">
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Email / WhatsApp message log */}
      <Card>
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Messages</h2>
          {messages.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No emails or WhatsApp messages sent for this invoice yet.</p>
          ) : (
            <>
            <ul className="sm:hidden divide-y" data-testid="messages-phone-list">
              {messages.map((m) => (
                <li key={`m-${m.id}`} className="py-3 space-y-1 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{CHANNEL_LABEL[m.channel] || m.channel} · {m.kind === 'reminder' ? 'Reminder' : 'Invoice'}</p>
                    <StatusPill status={m.status} />
                  </div>
                  <p className="text-xs text-gray-500">{fmtWhen(m.created_at)} · {!m.created_by || m.created_by === 'auto' ? 'Automatic' : m.created_by}</p>
                  <p className="text-xs text-gray-600 break-all">{m.to || '—'}</p>
                  {m.error && m.status !== 'sent' && <p className="text-xs text-gray-500">{m.error}</p>}
                  {m.pdf_id && (
                    <button type="button" className="inline-flex items-center gap-1 text-xs text-gray-700 underline underline-offset-2 py-1" onClick={() => downloadSentPdf(m.pdf_id)}>
                      <FileText className="w-3.5 h-3.5" /> PDF that was sent
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <tr>
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Channel</th>
                    <th className="py-2 pr-4">To</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">PDF sent</th>
                    <th className="py-2 pr-4">By</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr key={m.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-4 whitespace-nowrap">{fmtWhen(m.created_at)}</td>
                      <td className="py-2 pr-4">{m.kind === 'reminder' ? 'Reminder' : 'Invoice'}</td>
                      <td className="py-2 pr-4">{CHANNEL_LABEL[m.channel] || m.channel}</td>
                      <td className="py-2 pr-4 text-gray-600 break-all">{m.to || '—'}</td>
                      <td className="py-2 pr-4">
                        <StatusPill status={m.status} />
                        {m.error && m.status !== 'sent' && <p className="text-xs text-gray-500 mt-1">{m.error}</p>}
                        {m.status === 'unknown' && !m.error && <p className="text-xs text-gray-500 mt-1">No answer from the provider. Check before sending again.</p>}
                      </td>
                      <td className="py-2 pr-4">
                        {m.pdf_id ? (
                          <button type="button" className="inline-flex items-center gap-1 text-gray-700 underline underline-offset-2 hover:text-black"
                                  onClick={() => downloadSentPdf(m.pdf_id)} data-testid={`sent-pdf-${m.id}`}>
                            <FileText className="w-3.5 h-3.5" /> PDF
                          </button>
                        ) : '—'}
                      </td>
                      <td className="py-2 pr-4 text-gray-600">{!m.created_by || m.created_by === 'auto' ? 'Automatic' : m.created_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* PDF: phone browsers can't scroll an inline viewer, so offer the file itself */}
      <Card className="md:hidden">
        <CardContent className="p-4">
          <Button variant="outline" className="w-full" onClick={handleDownload} data-testid="open-pdf-phone">
            <Download className="w-4 h-4 mr-2" /> Open invoice PDF
          </Button>
        </CardContent>
      </Card>

      {/* PDF preview */}
      <Card className="hidden md:block">
        <CardContent className="p-0 h-[70vh]">
          {pdfUrl ? (
            <iframe title="Invoice PDF" src={pdfUrl} className="w-full h-full border-0 rounded-md" />
          ) : pdfError ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-sm text-gray-500">
              <p>Couldn't load the PDF preview.</p>
              <Button size="sm" variant="outline" onClick={loadPdf}>Retry</Button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          )}
        </CardContent>
      </Card>

      {/* Record payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>
              Against invoice <span className="font-medium">{invoice.invoice_number}</span> · Balance due {formatINR(invoice.balance_due)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPay} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="pay-amount">Amount (₹)</Label>
                <Input
                  id="pay-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                  required
                />
                <p className="text-xs text-gray-500">Balance due {formatINR(invoice.balance_due)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay-date">Payment date</Label>
                <DatePicker
                  id="pay-date"
                  value={payForm.payment_date}
                  onChange={(v) => setPayForm({ ...payForm, payment_date: v })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="pay-method">Method</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger id="pay-method" data-testid="pay-method-select">
                    <SelectValue placeholder="Pick a method (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay-account">Deposited to</Label>
                <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v })}>
                  <SelectTrigger id="pay-account">
                    <SelectValue placeholder="Pick an account (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {openAccounts.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name} {a.last4 ? `· ••${a.last4}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-ref">Reference / UTR (optional)</Label>
              <Input
                id="pay-ref"
                value={payForm.reference}
                onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                placeholder="e.g. UTR123456789 or cheque #00045"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-notes">Notes (optional)</Label>
              <Textarea
                id="pay-notes"
                value={payForm.notes}
                onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPayOpen(false)} disabled={paySaving} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-black/90 text-white w-full sm:w-auto" disabled={paySaving}>
                {paySaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Record payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send invoice / reminder dialog */}
      <Dialog open={!!sendMode} onOpenChange={(o) => { if (!o && !sending) setSendMode(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {sendMode === 'reminder' ? 'Send payment reminder'
                : sendForm.email && !sendForm.whatsapp ? 'Email invoice'
                : sendForm.whatsapp && !sendForm.email ? 'Send invoice on WhatsApp'
                : 'Send invoice'}
            </DialogTitle>
            <DialogDescription>
              <span className="font-medium">{invoice.invoice_number}</span> · {invoice.client_name}
              {sendMode === 'reminder' ? ` · Balance due ${formatINR(invoice.balance_due)}` : ' · PDF attached'}
            </DialogDescription>
          </DialogHeader>
          {sendResults ? (
            <div className="space-y-4">
              <ul className="space-y-2">
                {sendResults.map((r) => (
                  <li key={r.channel} className="flex items-start justify-between gap-3 border rounded-md p-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium">{CHANNEL_LABEL[r.channel] || r.channel}</p>
                      {r.to && <p className="text-xs text-gray-500 break-all">{r.to}</p>}
                      {r.error && <p className="text-xs text-gray-700 mt-1">{r.error}</p>}
                    </div>
                    <StatusPill status={r.status} />
                  </li>
                ))}
              </ul>
              <div className="flex justify-end">
                <Button type="button" className="bg-black hover:bg-black/90 text-white w-full sm:w-auto" onClick={() => setSendMode(null)}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitSend} className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="send-email"
                    checked={sendForm.email}
                    onCheckedChange={(v) => setSendForm((f) => ({ ...f, email: v === true }))}
                  />
                  <Label htmlFor="send-email">Email</Label>
                </div>
                {sendForm.email && (
                  <Input
                    value={sendForm.emailTo}
                    onChange={(e) => setSendForm((f) => ({ ...f, emailTo: e.target.value }))}
                    placeholder="name@company.com (comma-separate for more)"
                    disabled={sendPrefilling}
                  />
                )}
                {sendForm.email && !sendPrefilling && recip && !recip.email && !sendForm.emailTo && (
                  <p className="text-xs text-gray-600">No email address on file for this client. Type one above.</p>
                )}
                {sendForm.email && (
                  <p className="text-xs text-gray-500">
                    Subject: {sendMode === 'reminder' ? `Payment reminder: invoice ${invoice.invoice_number}` : `Invoice ${invoice.invoice_number} from DESENO`} · PDF attached
                  </p>
                )}
                {emailWarn && <p className="text-xs text-gray-600">{emailWarn}</p>}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="send-whatsapp"
                    checked={sendForm.whatsapp}
                    onCheckedChange={(v) => setSendForm((f) => ({ ...f, whatsapp: v === true }))}
                  />
                  <Label htmlFor="send-whatsapp">WhatsApp</Label>
                </div>
                {sendForm.whatsapp && (
                  <Input
                    type="tel"
                    value={sendForm.phoneTo}
                    onChange={(e) => setSendForm((f) => ({ ...f, phoneTo: e.target.value }))}
                    placeholder="+91 98xxx xxxxx"
                    disabled={sendPrefilling}
                  />
                )}
                {sendForm.whatsapp && !sendPrefilling && recip && !recip.whatsapp && !sendForm.phoneTo && (
                  <p className="text-xs text-gray-600">No usable WhatsApp number on file for this client. Type one above.</p>
                )}
                {sendForm.whatsapp && (
                  <p className="text-xs text-gray-500">
                    {sendMode === 'reminder'
                      ? 'A payment reminder message'
                      : 'The invoice PDF is attached automatically'}
                    {wa?.phone ? ` · sent from ${wa.phone}` : ''}
                  </p>
                )}
                {waWarn && <p className="text-xs text-gray-600">{waWarn}</p>}
              </div>
              {sendPrefilling && <p className="text-xs text-gray-400">Loading the client's contact details…</p>}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setSendMode(null)} disabled={sending} className="w-full sm:w-auto">
                  Cancel
                </Button>
                <Button type="submit" className="bg-black hover:bg-black/90 text-white w-full sm:w-auto whitespace-nowrap" disabled={sending || sendPrefilling}>
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {sendMode === 'reminder' ? 'Send reminder'
                    : sendForm.email && !sendForm.whatsapp ? 'Send email'
                    : sendForm.whatsapp && !sendForm.email ? 'Send on WhatsApp'
                    : 'Send invoice'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
